import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Container,
  Divider,
  Paper,
  TextField,
  Typography,
  Snackbar,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Link,
  List,
  ListItem
} from '@mui/material';
import { endOfDay, format } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';

import { AuthContext, AuthContextType } from 'src/contexts/AuthContext';
import { useDeleteQuickLogActivityMutation } from 'src/graphql/generated';
import { useErrorHandler } from 'src/hooks/useErrorHandler';
import { colors } from 'src/components/App/colors';
import { SaveQuickLogActivityMutation, useGetReferencesQuery } from 'src/graphql/generated';
import { Attachments } from 'src/components/Attachments/Attachments';
import { QuickLogFormData } from './types';
import { useSubmitQuickLog } from './useSubmitQuickLog';
import { CPD_TOTAL_HOUR_LIMIT } from 'src/components/Attachments/constants';
import { clamp } from 'lodash';
import { CPDTypeInputSection } from './CPDInputSection';
import { Section } from './Section';
import { Button } from 'src/components/Button/Button';
import { ContentPageTitle } from '../../ContentPageTitle/ContentPageTitle';
import { LoadableBox } from '../../Loadable/LoadableBox';
import { AttachmentsArray } from './types';
import { Alerts } from './Alerts';
import { useMemberActivity } from './useMemberActivity';
import { nanToZero, roundHalf } from 'src/components/Attachments/utils';
import { ConfirmDelete } from './ConfirmDelete';
import sortBy from 'lodash/sortBy';
import { RolesRestricted } from 'src/components/RequiredAuth/RolesAuthRoute';
import { Tooltip } from './Tooltip';

type LogProps = {
  id?: string;
  cpdId?: string;
  urlFormData?: QuickLogFormData;
};

const Log = ({ id, cpdId, urlFormData }: LogProps) => {
  const earef = useRef<HTMLInputElement>();
  const rpref = useRef<HTMLInputElement>();
  const moref = useRef<HTMLInputElement>();

  const editing = !!id || !!cpdId;

  const { activeMemberId } = React.useContext(AuthContext) as AuthContextType;
  const { cpdRole } = useContext(AuthContext) as AuthContextType;
  const { isCpdPilot } = useContext(AuthContext) as AuthContextType;
  const history = useHistory();

  let maxDate: Date;
  if (isCpdPilot) {
    maxDate = new Date('03/31/2027');
  } else if (cpdRole === 'member') {
    maxDate = endOfDay(new Date());
  }
  let minAllowedDate: Date;
  if (cpdRole === 'staff') {
    minAllowedDate = new Date('01/02/2023');
  } else if (cpdRole === 'member') {
    minAllowedDate = new Date('01/02/2026');
  }

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = React.useState(false);

  const handleError = useErrorHandler();

  const [deleteQuickLogActivityMutation, { error: deleteError }] =
    useDeleteQuickLogActivityMutation();

  const { data: referenceData } = useGetReferencesQuery({ onError: handleError });

  const scrollRef = useRef<HTMLElement>(null);

  const { register, handleSubmit, control, watch, formState, setValue, reset } =
    useForm<QuickLogFormData>({
      defaultValues: {
        activitykey: null,
        activity_title: '',
        activity_details: '',
        activity_date: null,
        ea_hours: 0,
        rp_hours: 0,
        mo_hours: 0,
        total_hours: 0,
        program_levels: [],
        notes: '',
        attachments: [],
        agreedToDeclaration: false
      },
      mode: 'onChange'
    });

  const displayPlrQuestion = (watch('activity_date') ?? new Date()) >= new Date('2024-01-01');
  const display2027Requirements = (watch('activity_date') ?? new Date()) >= new Date('2027-01-01');

  const notesValue = watch('notes');
  const attachmentsValue = watch('attachments');
  const isDirtyNotes = Boolean(notesValue && notesValue.length > 0);
  const isDirtyEvidence = Boolean(attachmentsValue && attachmentsValue.length > 0);
  const isNotesRequired = Boolean(display2027Requirements && !isDirtyEvidence);
  const isAttachmentsRequired = Boolean(display2027Requirements && !isDirtyNotes);
  const { memberActivityData, error: activityError } = useMemberActivity({
    activityKey: id,
    cpdId: cpdId,
    memberId: activeMemberId
  });

  const scrollToTop = useCallback(() => {
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }, 10);
  }, []);

  const handlePrlChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const valueCopy = [...watch().program_levels];
    const clickedValue = event.target.value;
    if (checked) {
      valueCopy.push(clickedValue); // append to array
    } else {
      const idx = valueCopy.findIndex((selectedPlr) => selectedPlr === clickedValue);
      valueCopy.splice(idx, 1); // remove from array
    }

    setValue('program_levels', valueCopy, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const loading = editing && !memberActivityData && !activityError;
  const activityPending =
    editing && memberActivityData && !memberActivityData.getMemberActivityByKey?.cpd_id;

  const formDisabled = activityPending || !!activityError;

  const copyLogURL = () => {
    setSnackBarOpen(true);
    const url = new URL(`${window.location.origin}/log`);
    url.searchParams.set('logName', watch('activity_title'));
    if (watch('activity_details')) {
      url.searchParams.set('activity_details', watch('activity_details'));
    }
    if (watch('ea_hours') > 0) {
      url.searchParams.set('ea_hours', watch('ea_hours').toString());
    }
    if (watch('rp_hours') > 0) {
      url.searchParams.set('rp_hours', watch('rp_hours').toString());
    }
    if (watch('mo_hours') > 0) {
      url.searchParams.set('mo_hours', watch('mo_hours').toString());
    }
    const date = watch('activity_date');
    if (date) {
      url.searchParams.set('date', format(date, 'yyyy-MMM-dd HH:mm:ss'));
    }
    const program_levels = watch('program_levels');
    if (program_levels.length > 0) {
      url.searchParams.set('program_levels', program_levels.join('|'));
    }
    if (watch('notes')) {
      url.searchParams.set('notes', watch('notes'));
    }
    if (watch('agreedToDeclaration')) {
      url.searchParams.set('agreedToDeclaration', watch('agreedToDeclaration').toString());
    }
    navigator.clipboard.writeText(url.toString());
  };
  const handleSnackBarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackBarOpen(false);
  };

  const deleteQuickLog = () => {
    deleteQuickLogActivityMutation({
      variables: {
        memberid: activeMemberId,
        activitykey: id
      },
      onCompleted: (data) => {
        if (data.deleteQuickLogActivity?.activitykey) {
          history.push('/history', { replace: true, state: { deleteId: id } });
        }
      },
      onError: () => {
        scrollToTop();
      }
    });
  };

  const onPressDelete = () => {
    setDeleteDialogOpen(true);
  };

  const onConfirmDelete = () => {
    setDeleteDialogOpen(false);
    deleteQuickLog();
  };

  useEffect(() => {
    //reset form to member activity data when it's available, so isDirty will be false after reset
    if (editing && memberActivityData?.getMemberActivityByKey) {
      const {
        activitykey,
        title,
        date,
        cpd_types,
        hours,
        description,
        attachments,
        program_levels,
        activity_details,
        agreedtoDeclaration
      } = memberActivityData?.getMemberActivityByKey;
      reset({
        activitykey: activitykey,
        activity_title: title || '',
        activity_details: activity_details || '',
        activity_date: date ? new Date(date) : null,
        ea_hours: cpd_types?.find((cpdType) => cpdType?.type === 'EA')?.hours || 0,
        rp_hours: cpd_types?.find((cpdType) => cpdType?.type === 'RP')?.hours || 0,
        mo_hours: cpd_types?.find((cpdType) => cpdType?.type === 'MO')?.hours || 0,
        total_hours: hours,
        program_levels: (program_levels as string[]) || [],
        notes: description || '',
        attachments: (attachments as AttachmentsArray) || [],
        agreedToDeclaration: agreedtoDeclaration || false
      });
    }
  }, [editing, memberActivityData, reset]);

  const {
    submit,
    data: submissionResponse,
    loading: submitting,
    error: submissionError,
    reset: resetSubmissionMutation
  } = useSubmitQuickLog({
    onCompleted: (data: SaveQuickLogActivityMutation) => {
      if (editing) {
        history.push('/history', {
          updatedId: data.saveQuickLogActivity?.activitykey
        });
      } else {
        reset();
        scrollToTop();
      }
    },
    onError: (e: any) => {
      scrollToTop();
      console.error('submit failed');
      console.error(e);
    }
  });

  const submitForm = handleSubmit((data) => {
    submit(data);
  });

  const activityTitleOptions = useMemo(() => {
    if (!referenceData?.activity_title?.items) {
      return undefined;
    }
    return sortBy(referenceData.activity_title.items, (item) => item?.value).map((i) => ({
      label: i?.value,
      key: i?.key
    }));
  }, [referenceData]);

  const updateTotalHours = useCallback(() => {
    setValue('total_hours', watch('mo_hours') + watch('rp_hours') + watch('ea_hours'), {
      shouldValidate: true,
      shouldDirty: true
    });
  }, [setValue, watch]);

  const roundField = useCallback(
    (fieldName: 'ea_hours' | 'rp_hours' | 'mo_hours') => {
      setValue(fieldName, clamp(roundHalf(watch(fieldName)), 0, 500), {
        shouldDirty: true,
        shouldValidate: true
      });
      updateTotalHours();
    },
    [setValue, updateTotalHours, watch]
  );

  const setTitle = (title: string) => {
    setValue('activity_title', title || '', {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  // Effect to load URL data
  useEffect(() => {
    if (urlFormData) {
      // activity date is not validating after using reset - do this manually
      setValue('activity_date', urlFormData.activity_date, { shouldValidate: true });
      // reset the form to use the URL form data
      reset(urlFormData, { keepDefaultValues: true });
      // reset does not trigger re-calculation of totalhours so do it manually
      const newTotalHours = urlFormData.mo_hours + urlFormData.rp_hours + urlFormData.ea_hours;
      setValue('total_hours', newTotalHours);
    }
  }, [urlFormData, reset, setValue]);

  // this needs to be destructured like this, writing formState.isValid will cause the value to be ignored on first render for some reason
  const { isValid, isDirty } = formState;

  return (
    <>
      <Box ref={scrollRef}>
        <ContentPageTitle currentPageTitle="Quick Log" />
      </Box>
      <form>
        <Container
          maxWidth={'lg'}
          sx={{
            textAlign: 'start',
            mt: 3
          }}
        >
          <Alerts
            activityPending={activityPending}
            onDismissSuccessfulSubmission={resetSubmissionMutation}
            submissionError={submissionError}
            submissionResponse={submissionResponse}
            activityError={activityError}
            deleteError={deleteError}
          />

          <ConfirmDelete
            onClose={() => setDeleteDialogOpen(false)}
            onConfirm={onConfirmDelete}
            isOpen={deleteDialogOpen}
          />

          <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, pb: 4 }}>
            <Typography sx={{ color: colors['charcoal.700'] }} variant={'body2'}>
              Easily record the activities that help you keep your professional knowledge and skills
              current or that build expertise within your scope of practice.
            </Typography>
            <Alert sx={{ my: 1.5 }} severity={'info'}>
              <AlertTitle>
                <Typography variant={'body2'} component={'div'}>
                  To avoid duplication, do not log activities completed with an RACGP CPD provider,
                  as these will be automatically recorded for you within a fortnight of completion.
                </Typography>
              </AlertTitle>
            </Alert>
            <Divider sx={{ color: colors['stone.100'], mb: 3 }} />
            <Section title={'Activity Details'} required>
              {display2027Requirements && (
                <LoadableBox sx={{ width: '100%', mb: 4 }} loading={loading}>
                  <Controller
                    control={control}
                    name={'activity_details'}
                    rules={{ required: true }}
                    render={({ fieldState: {}, field: { value } }) => (
                      <TextField
                        {...register('activity_details')}
                        value={value}
                        fullWidth
                        label="Activity title *"
                        disabled={formDisabled}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </LoadableBox>
              )}
              <Controller
                control={control}
                name={'activity_title'}
                rules={{ required: true }}
                render={({ field: { value, name, ref } }) => {
                  return (
                    <LoadableBox sx={{ width: '100%', mb: 4 }} loading={loading}>
                      <Autocomplete
                        ref={ref}
                        disablePortal
                        disabled={formDisabled}
                        getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
                        id={name}
                        freeSolo
                        options={activityTitleOptions || []}
                        onInputChange={(_e: any, value, reason) => {
                          if (reason === 'input') {
                            setTitle(value);
                          }
                        }}
                        onChange={(_e, value: any, _reason) => {
                          setTitle(value?.label);
                        }}
                        value={value}
                        renderInput={(params) => (
                          <TextField
                            value={value}
                            ref={params.InputProps.ref}
                            label={`Activity ${!display2027Requirements ? 'title ' : 'type '}  *`}
                            {...params}
                          />
                        )}
                      />
                    </LoadableBox>
                  );
                }}
              />

              <Controller
                control={control}
                name={'activity_date'}
                rules={{
                  required: true,
                  validate: {
                    required: (value) => !!value,
                    activityDateAllowed: (value) => {
                      if (!value) return;
                      const isactivityDateAllowed = new Date(value) >= minAllowedDate;
                      if (!isactivityDateAllowed) {
                        return (
                          'Activity date must be on or after ' +
                          format(minAllowedDate, 'dd/MM/yyyy') +
                          '.'
                        );
                      }
                    },
                    isPast: (value) => {
                      if (!value) return;
                      const isPast = new Date(value) <= maxDate;
                      if (!isPast) {
                        return `Activity date must be on or before ${format(
                          new Date(),
                          'dd/MM/yyyy'
                        )}.`;
                      }
                    }
                  }
                }}
                render={({ fieldState: { error }, field: { value, ref } }) => (
                  <LoadableBox loading={loading}>
                    <DatePicker
                      openTo="year"
                      ref={ref}
                      inputFormat={'dd/MM/yyyy'}
                      views={['day']}
                      value={value || null}
                      onChange={(e) => {
                        setValue('activity_date', e, {
                          shouldValidate: true,
                          shouldDirty: true
                        });
                      }}
                      disabled={formDisabled}
                      minDate={minAllowedDate}
                      maxDate={maxDate}
                      renderInput={(params) => (
                        <TextField
                          sx={{ width: { xs: '100%', sm: '50%' } }}
                          {...params}
                          label="Activity completion date *"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </LoadableBox>
                )}
              />

              <Typography color={'primary'} variant={'body1'} mt={4} mb={0.5}>
                Type of CPD*
              </Typography>
              <Typography sx={{ color: colors['charcoal.700'] }} variant={'body2'}>
                Log hours against any CPD types that apply to your activity. See our CPD activity
                types guide{' '}
                <Link
                  href="https://www1.racgp.org.au/getmedia/31e42c2d-7f6c-42c3-8b74-80ba76d88d9e/CPD-activity-types-guide.pdf.aspx"
                  target="_blank"
                >
                  here
                </Link>
                . Hours will be totalled in increments of 30 minutes (0.5).
              </Typography>

              <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                <CPDTypeInputSection
                  title={'Educational Activities'}
                  loading={loading}
                  tooltip={
                    'Educational Activities (EA) are activities that expand your general practice knowledge, skills, and attitudes, related to your scope of practice'
                  }
                >
                  <TextField
                    {...register('ea_hours', {
                      setValueAs: (v) => nanToZero(Number.parseFloat(v)),
                      onBlur: () => {
                        roundField('ea_hours');
                      }
                    })}
                    sx={{ width: '100%' }}
                    type={'number'}
                    disabled={formDisabled}
                    inputProps={{ min: 0, step: 0.5, max: 500 }}
                    InputLabelProps={{ shrink: true }}
                    label={'Hours'}
                    defaultValue={0}
                    inputRef={earef}
                    onClick={() => earef.current?.focus()}
                  ></TextField>
                </CPDTypeInputSection>

                <CPDTypeInputSection
                  title={'Reviewing Performance'}
                  loading={loading}
                  tooltip={
                    'Reviewing Performance (RP) are activities that require reflection on feedback about your work'
                  }
                >
                  <TextField
                    {...register('rp_hours', {
                      setValueAs: (v) => nanToZero(Number.parseFloat(v)),
                      onBlur: () => {
                        roundField('rp_hours');
                      }
                    })}
                    sx={{ width: '100%' }}
                    type={'number'}
                    disabled={formDisabled}
                    inputProps={{ min: 0, step: 0.5 }}
                    InputLabelProps={{ shrink: true }}
                    label={'Hours'}
                    inputRef={rpref}
                    onClick={() => rpref.current?.focus()}
                  ></TextField>
                </CPDTypeInputSection>

                <CPDTypeInputSection
                  title={'Measuring Outcomes'}
                  loading={loading}
                  tooltip={
                    'Measuring Outcomes (MO) are activities that use your work data to ensure quality results'
                  }
                >
                  <TextField
                    {...register('mo_hours', {
                      setValueAs: (v) => nanToZero(Number.parseFloat(v)),
                      onBlur: () => {
                        roundField('mo_hours');
                      }
                    })}
                    sx={{ width: '100%' }}
                    type={'number'}
                    disabled={formDisabled}
                    inputProps={{ min: 0, step: 0.5 }}
                    InputLabelProps={{ shrink: true }}
                    label={'Hours'}
                    inputRef={moref}
                    onClick={() => moref.current?.focus()}
                  ></TextField>
                </CPDTypeInputSection>

                <Divider sx={{ my: 2 }} />

                <Controller
                  control={control}
                  name={'total_hours'}
                  rules={{
                    min: { value: 0.5, message: '' },
                    max: {
                      value: CPD_TOTAL_HOUR_LIMIT,
                      message: `${CPD_TOTAL_HOUR_LIMIT} CPD hour limit per Quick Log`
                    },
                    required: true
                  }}
                  render={({ fieldState, field: { value } }) => {
                    const showError = !!fieldState.error?.message;

                    return (
                      <Box>
                        <Box display={'flex'} justifyContent={'space-between'}>
                          <Typography
                            variant={'body1'}
                            fontWeight={'bold'}
                            sx={{ color: colors['charcoal.900'] }}
                          >
                            Total:
                          </Typography>
                          <Typography
                            variant={'body1'}
                            fontWeight={'bold'}
                            sx={{
                              color: showError ? colors['red.700'] : colors['darkTeal.700']
                            }}
                          >
                            {value}
                            {' CPD hours'}
                          </Typography>
                        </Box>
                        {showError && (
                          <Box display={'flex'} justifyContent={'flex-end'}>
                            <Typography
                              variant={'caption'}
                              color={colors['red.700']}
                              alignSelf={'flex-end'}
                            >
                              {fieldState.error?.message}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  }}
                />
              </Box>
            </Section>
            {displayPlrQuestion && (
              <>
                <Divider sx={{ my: 4 }} />

                <Section title={'Program-Level Requirements'} required={false}>
                  <Typography sx={{ color: colors['charcoal.700'], mb: 2 }} variant={'body2'}>
                    To meet the MBA CPD registration standard, select the program-level
                    requirement/s relevant to this activity, where&nbsp;applicable.
                  </Typography>
                  <LoadableBox sx={{ mb: 2 }} loading={loading}>
                    <FormGroup sx={{ alignItems: 'flex-start' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={
                            <Checkbox
                              value="CSP"
                              checked={watch().program_levels.some(
                                (selectedPlr) => selectedPlr === 'CSP'
                              )}
                              onChange={handlePrlChange}
                            />
                          }
                          label="Culturally Safe Practice"
                        />
                        <Tooltip tooltip="Cultural safety is determined by Aboriginal and Torres Strait Islander individuals, families and communities" />
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={
                            <Checkbox
                              value="HI"
                              checked={watch().program_levels.some(
                                (selectedPlr) => selectedPlr === 'HI'
                              )}
                              onChange={handlePrlChange}
                            />
                          }
                          label="Health Inequities"
                        />
                        <Tooltip tooltip="Health inequities are systematic differences in the opportunities groups have to achieve optimal health, leading to unfair avoidable differences in health outcomes" />
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={
                            <Checkbox
                              value="EP"
                              checked={watch().program_levels.some(
                                (selectedPlr) => selectedPlr === 'EP'
                              )}
                              onChange={handlePrlChange}
                            />
                          }
                          label="Professionalism and Ethical Practice"
                        />
                        <Tooltip tooltip="Doctors have a duty to make the care of patients their first concern and practice medicine safely and effectively" />
                      </Box>
                    </FormGroup>
                  </LoadableBox>
                </Section>
              </>
            )}
            <Divider sx={{ my: 4 }} />
            <Box
              sx={[
                isDirty &&
                  editing &&
                  watch().notes.length > 1000 && {
                    'p, span': {
                      color: 'red'
                    }
                  }
              ]}
            >
              <Section title={'Reflection'} required={isNotesRequired ? true : false}>
                <Typography
                  sx={{ color: colors['charcoal.700'] + '!important', mb: 2 }}
                  variant={'body2'}
                >
                  Provide evidence to support the CPD type(s) and hours claimed by entering relevant
                  details in the Notes section or upload supporting documentation.
                  <br />
                  <br />
                  What did you learn? What changes would you make to your practise as a result?
                </Typography>
                <LoadableBox sx={{ mb: 2 }} loading={loading}>
                  <Controller
                    control={control}
                    name={'notes'}
                    rules={{
                      maxLength: 1000,
                      required: isNotesRequired ? true : false
                    }}
                    render={({ fieldState: {}, field: { value } }) => (
                      <TextField
                        {...register('notes')}
                        value={value}
                        fullWidth
                        label="Notes"
                        disabled={formDisabled}
                        multiline
                        inputProps={{ maxLength: 1000 }}
                        minRows={5}
                        maxRows={15}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </LoadableBox>
                <Typography component="p" variant="caption" align="right" sx={{ mt: 1 }}>
                  {watch().notes.length}/1000
                </Typography>
              </Section>
            </Box>
            <Divider sx={{ my: 4 }} />
            <Section title={'Evidence'} required={isAttachmentsRequired ? true : false}>
              <Typography
                sx={{ color: colors['charcoal.700'], mb: 2, a: { textDecoration: 'underline' } }}
                variant={'body2'}
              >
                You do not need to provide any further evidence in this application but ensure you
                keep adequate records of your activity/ies as you may be audited by the RACGP or
                Medical Board of Australia (MBA). The MBA requires evidence of your annual CPD
                activities to be retained for three years. See evidence guide{' '}
                <a
                  href="https://www.racgp.org.au/getmedia/726d015d-c7ed-488d-94bf-e708559f3f75/CPD-Activities-Evidence-Guide.pdf.aspx"
                  target="_blank"
                  rel="noreferrer"
                >
                  here.
                </a>
              </Typography>

              {!formDisabled && (
                <Controller
                  control={control}
                  name={'attachments'}
                  rules={{ max: 5, required: isAttachmentsRequired ? true : false }}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <LoadableBox loading={loading}>
                        <Attachments
                          onChangeAttachments={onChange}
                          attachments={value}
                          isEditing={editing}
                          uploadText="EVIDENCE"
                        />
                      </LoadableBox>
                    );
                  }}
                />
              )}
            </Section>
            {display2027Requirements && (
              <>
                <Divider sx={{ my: 4 }} />
                <Section title={'Declaration'} required={true}>
                  <Typography sx={{ mb: 1 }}>
                    I acknowledge and agree to this declaration.
                  </Typography>
                  <Typography sx={{ mb: 1 }}>I declare the following:</Typography>

                  <List
                    sx={{
                      listStyleType: 'disc',
                      width: '100%',
                      listStylePosition: 'outside',
                      pl: 3
                    }}
                  >
                    <ListItem sx={{ display: 'list-item', p: 0 }}>I acknowledge that:</ListItem>
                    <ListItem sx={{ display: 'list-item', p: 0 }}>
                      RACGP may contact me directly to obtain further information relating to this
                      activity for the purposes of undertaking a Quality Assurance Assessment;
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', p: 0 }}>
                      RACGP may disclose the information contained in this Quick Log form to Ahpra
                      and the Medical Board of Australia;
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', p: 0 }}>
                      RACGP reserves the right to amend my CPD record if this activity does not meet
                      relevant Quality Assurance Assessment requirements; and{' '}
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', p: 0 }}>
                      RACGP may combine de-identified data contained in this Quick log form with
                      other RACGP CPD data for the purposes of undertaking internal trends analysis.{' '}
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', p: 0 }}>
                      The information I have provided in this Quick Log form is complete, true and
                      accurate.
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', p: 0 }}>
                      I acknowledge that providing false or misleading information in this Quick Log
                      form may constitute Academic Misconduct within the meaning of the{' '}
                      <Link
                        rel="noreferrer"
                        href="https://www.racgp.org.au/education/gp-training/gp-training/education-policy-and-supporting-documents/training-program-policies/academic-misconduct"
                        target="_blank"
                      >
                        RACGP Academic Misconduct Policy
                      </Link>{' '}
                      or may contravene the{' '}
                      <Link
                        rel="noreferrer"
                        href="https://www.racgp.org.au/the-racgp/governance/organisational-policies/member-code-of-conduct"
                      >
                        Membership Code of Conduct.
                      </Link>
                    </ListItem>
                  </List>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Controller
                      name="agreedToDeclaration"
                      control={control}
                      rules={{ required: cpdRole === 'staff' ? false : true }}
                      defaultValue={false}
                      render={({ field: { value, onChange, ...field } }) => (
                        <Checkbox
                          {...field}
                          checked={!!value}
                          onChange={(e) => onChange(e.target.checked)}
                          disabled={cpdRole === 'staff'}
                        />
                      )}
                    />
                    <Typography sx={{ color: '#6f6f6f' }}>I agree</Typography>
                  </Box>
                </Section>
              </>
            )}
            <Divider sx={{ my: 4 }} />
            <Box display={'flex'} gap={2} justifyContent={{ xs: 'center', md: 'flex-end' }}>
              <RolesRestricted roles={['staff']} hideChildComponent={true}>
                <Button
                  sx={{ width: '100%', maxWidth: 180, height: 40, marginRight: 'auto' }}
                  color="primary"
                  variant={editing ? 'contained' : 'outlined'}
                  disabled={submitting || formDisabled}
                  onClick={copyLogURL}
                >
                  Share Log URL
                </Button>
                <Snackbar open={snackBarOpen} autoHideDuration={6000} onClose={handleSnackBarClose}>
                  <Alert onClose={handleSnackBarClose} severity="success" sx={{ width: '100%' }}>
                    Log URL copied
                  </Alert>
                </Snackbar>
              </RolesRestricted>
              <Button
                sx={{ width: '100%', maxWidth: 180, height: 40 }}
                color="primary"
                variant={editing ? 'contained' : 'outlined'}
                disabled={submitting || formDisabled}
                onClick={() => {
                  editing ? onPressDelete() : reset();
                }}
              >
                {editing ? 'Delete' : 'Clear'}
              </Button>
              <Button
                sx={{ width: '100%', maxWidth: 180, height: 40 }}
                color="primary"
                variant="contained"
                type={'submit'}
                disabled={!isValid || !isDirty || formDisabled}
                onClick={submitForm}
                loading={submitting}
              >
                {editing ? 'Update' : 'Submit'}
              </Button>
            </Box>
          </Paper>
        </Container>
      </form>
    </>
  );
};

export { Log };
